import type { TrumpMode } from '@mendikot/engine';

export type AuthMethod = 'guest' | 'google' | 'email' | 'apple';
export type PlayMode = 'online' | 'practice';
type Flag = 0 | 1;

/**
 * Single analytics catalog for web and native (plan §5.1). GA4 limits: names ≤ 40 chars, ≤ 25 params,
 * values string|number, no PII. Booleans are sent as 0/1.
 */
export interface AnalyticsEvents {
  pwa_install_prompt_shown: Record<string, never>;
  pwa_install: Record<string, never>;
  app_open: { launch_mode: 'browser' | 'standalone' | 'native' };
  login: { method: AuthMethod };
  sign_up: { method: AuthMethod };
  guest_upgraded: { method: AuthMethod };
  auth_error: { code: string; method: AuthMethod };
  consent_updated: { analytics: Flag };
  room_created: { preset: string; trump_mode: TrumpMode; player_count: number };
  invite_shared: { channel: 'web_share' | 'copy' | 'whatsapp' };
  invite_opened: { via: 'link' | 'code' };
  room_joined: { via: 'code' | 'link' | 'rejoin'; player_count: number };
  room_filled: { fill_ms: number; player_count: number };
  match_start: {
    mode: PlayMode;
    session_match_index: number;
    preset: string;
    trump_mode: TrumpMode;
    player_count: number;
    bots: number;
  };
  first_card_played: { mode: PlayMode; ms_since_deal: number };
  trick_completed: { mode: PlayMode; trick_index: number; won: Flag; tens: number };
  hand_completed: {
    mode: PlayMode;
    result: 'win' | 'loss' | 'draw';
    tens_split: string;
    coat: Flag;
    whitewash: Flag;
    hand_ms: number;
  };
  coat_scored: { mode: PlayMode; mine: Flag; whitewash: Flag };
  match_completed: {
    mode: PlayMode;
    result: 'win' | 'loss';
    hands: number;
    duration_ms: number;
    bot_assisted: number;
  };
  rematch_vote: { yes: Flag };
  match_abandoned: { mode: PlayMode; stage: 'lobby' | 'reserve' | 'play' | 'result' };
  command_rejected: { code: string; command: string };
  disconnect: { in_game: Flag };
  reconnect: { gap_ms: number; ok: Flag };
  bot_takeover: { reason: string; mine: Flag };
  turn_timeout: { mine: Flag };
  nudge_sent: Record<string, never>;
  practice_start: { difficulty: 'easy' | 'normal'; trump_mode: TrumpMode };
  practice_complete: { result: 'win' | 'loss'; hands: number };
  tutorial_step: { step: number };
  tutorial_complete: Record<string, never>;
  theme_applied: { theme_id: string; source: 'remote' | 'default' | 'user' };
  push_prompt_shown: Record<string, never>;
  push_permission: { result: 'granted' | 'denied' | 'default' };
  notification_open: { type: string; nid: string };
  notification_foreground: { type: string };
}

export type AnalyticsEventName = keyof AnalyticsEvents;

export interface UserProperties {
  auth_method: AuthMethod;
  preferred_preset: string;
  preferred_trump: TrumpMode;
  lang: string;
  theme_id: string;
  launch_mode: 'browser' | 'standalone' | 'native';
  push_opt_in: 'yes' | 'no';
  games_played_bucket: '0' | '1-4' | '5-19' | '20-99' | '100+';
  app_platform: 'web' | 'ios' | 'android';
}

export function gamesPlayedBucket(count: number): UserProperties['games_played_bucket'] {
  if (count <= 0) return '0';
  if (count < 5) return '1-4';
  if (count < 20) return '5-19';
  if (count < 100) return '20-99';
  return '100+';
}

/** Performance trace names and their allowed attributes (≤ 5 each, plan §5.3). */
export const PERF_TRACES = {
  app_boot: ['launch_mode', 'theme', 'net_type'],
  auth_sign_in: ['method', 'net_type'],
  room_join: ['via', 'player_count', 'net_type'],
  card_play_rtt: ['preset', 'trump_mode', 'player_count', 'net_type', 'theme'],
  deal_render: ['player_count', 'theme', 'reduced_motion'],
  reconnect: ['cause', 'net_type'],
  theme_load: ['theme'],
} as const;

export type PerfTraceName = keyof typeof PERF_TRACES;
export type PerfAttribute<T extends PerfTraceName> = (typeof PERF_TRACES)[T][number];
